import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Divider,
  Chip,
  Avatar,
  Grid,
  Fade,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthContext } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { usePageTitle } from '../hooks/usePageTitle';
import logger from '../utils/logger';

/**
 * 招待情報インターフェース
 */
interface InvitationInfo {
  id: string;
  token: string;
  organizationName: string;
  organizationId: string;
  inviterName: string;
  inviterEmail: string;
  role: 'owner' | 'admin' | 'manager' | 'staff' | 'viewer';
  permissions: string[];
  invitedAt: Date;
  expiresAt: Date;
  message?: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
}

/**
 * ロール表示名のマッピング
 */
const ROLE_DISPLAY_NAMES = {
  owner: 'オーナー',
  manager: 'マネージャー',
  staff: 'スタッフ',
  admin: '管理者',
  viewer: '閲覧者',
} as const;

/**
 * ロール説明のマッピング
 */
const ROLE_DESCRIPTIONS = {
  owner: '全システムへのフルアクセス権限',
  manager: '店舗運営・スタッフ管理権限',
  staff: '予約・顧客管理権限',
  admin: 'システム管理・設定権限',
  viewer: '閲覧専用権限',
} as const;

/**
 * 招待承認・拒否ページコンポーネント
 * Route: /join/:invitationToken
 */
const JoinOrganization: React.FC = () => {
  usePageTitle('組織参加');
  const navigate = useNavigate();
  const { invitationToken } = useParams<{ invitationToken: string }>();
  const { isAuthenticated } = useAuthContext();
  const { acceptInvitation, declineInvitation } = useOrganization();

  // 状態管理
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * 招待情報の取得
   */
  const loadInvitationInfo = useCallback(async () => {
    if (!invitationToken) {
      setError('招待トークンが見つかりません');
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      logger.info('[JoinOrganization] Loading invitation info:', invitationToken);

      // AuthServiceから招待情報を取得
      const { default: AuthService } = await import('../services/authService');
      const result = await AuthService.validateInvitation(invitationToken);

      if (!result.success) {
        throw new Error(result.message || '招待情報の取得に失敗しました');
      }

      setInvitationInfo(result.invitation || null);
      logger.info('[JoinOrganization] Invitation info loaded successfully');

    } catch (err: any) {
      const errorMsg = err.message || '招待情報の取得中にエラーが発生しました';
      setError(errorMsg);
      logger.error('[JoinOrganization] Failed to load invitation info:', err);
    } finally {
      setIsLoading(false);
    }
  }, [invitationToken]);

  /**
   * 招待承認処理
   */
  const handleAcceptInvitation = useCallback(async () => {
    if (!invitationToken || !invitationInfo) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      logger.info('[JoinOrganization] Accepting invitation:', invitationToken);

      // useOrganizationフックを使用して招待を承認
      await acceptInvitation(invitationToken);

      setSuccess('招待を承認しました。組織に参加できました！');
      
      // 3秒後にダッシュボードにリダイレクト
      setTimeout(() => {
        navigate('/dashboard', {
          state: {
            welcomeMessage: `${invitationInfo.organizationName}へようこそ！`,
            isNewMember: true,
          }
        });
      }, 3000);

    } catch (err: any) {
      const errorMsg = err.message || '招待の承認中にエラーが発生しました';
      setError(errorMsg);
      logger.error('[JoinOrganization] Failed to accept invitation:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [invitationToken, invitationInfo, acceptInvitation, navigate]);

  /**
   * 招待拒否処理
   */
  const handleDeclineInvitation = useCallback(async () => {
    if (!invitationToken) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      logger.info('[JoinOrganization] Declining invitation:', invitationToken);

      // useOrganizationフックを使用して招待を拒否
      await declineInvitation(invitationToken);

      setSuccess('招待を拒否しました。');
      
      // 3秒後にホームページにリダイレクト
      setTimeout(() => {
        navigate(isAuthenticated ? '/dashboard' : '/login');
      }, 3000);

    } catch (err: any) {
      const errorMsg = err.message || '招待の拒否中にエラーが発生しました';
      setError(errorMsg);
      logger.error('[JoinOrganization] Failed to decline invitation:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [invitationToken, declineInvitation, navigate, isAuthenticated]);

  /**
   * 初期化処理
   */
  useEffect(() => {
    loadInvitationInfo();
  }, [loadInvitationInfo]);

  /**
   * 招待の有効期限チェック
   */
  const isExpired = invitationInfo && new Date() > new Date(invitationInfo.expiresAt);
  const isInvalidStatus = invitationInfo && invitationInfo.status !== 'pending';

  /**
   * ローディング状態の表示
   */
  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary">
            招待情報を確認しています...
          </Typography>
        </Box>
      </Container>
    );
  }

  /**
   * エラー状態の表示
   */
  if (error && !invitationInfo) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Fade in>
          <Card elevation={8}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3} alignItems="center">
                <CancelIcon sx={{ fontSize: 64, color: 'error.main' }} />
                <Typography variant="h4" align="center" gutterBottom>
                  招待が見つかりません
                </Typography>
                <Alert severity="error" sx={{ width: '100%' }}>
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
                  sx={{ mt: 2 }}
                >
                  {isAuthenticated ? 'ダッシュボードに戻る' : 'ログインページに戻る'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    );
  }

  /**
   * 成功状態の表示
   */
  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Fade in>
          <Card elevation={8}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
                <Typography variant="h4" align="center" gutterBottom>
                  完了しました
                </Typography>
                <Alert severity="success" sx={{ width: '100%' }}>
                  {success}
                </Alert>
                <LinearProgress sx={{ width: '100%', mt: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  自動的にリダイレクトします...
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    );
  }

  /**
   * メインの招待詳細表示
   */
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card elevation={8}>
          {isProcessing && <LinearProgress />}
          
          <CardContent sx={{ p: 4 }}>
            {/* ヘッダー */}
            <Stack spacing={3} alignItems="center" sx={{ mb: 4 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 64,
                  height: 64,
                }}
              >
                <BusinessIcon sx={{ fontSize: 32 }} />
              </Avatar>
              
              <Box textAlign="center">
                <Typography variant="h4" gutterBottom>
                  組織への招待
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  以下の組織への参加が招待されています
                </Typography>
              </Box>
            </Stack>

            {/* エラー表示 */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* 期限切れ・無効状態の表示 */}
            {(isExpired || isInvalidStatus) && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {isExpired 
                  ? 'この招待は期限切れです。新しい招待をリクエストしてください。'
                  : `この招待は既に${invitationInfo?.status === 'accepted' ? '承認' : invitationInfo?.status === 'cancelled' ? 'キャンセル' : '処理'}済みです。`
                }
              </Alert>
            )}

            {/* 招待詳細情報 */}
            {invitationInfo && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* 組織情報 */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <BusinessIcon color="primary" />
                        <Typography variant="h6">組織情報</Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          {invitationInfo.organizationName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          組織ID: {invitationInfo.organizationId}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* 招待者情報 */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, h: '100%' }}>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <PersonIcon color="primary" />
                        <Typography variant="h6">招待者</Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="subtitle1">
                          {invitationInfo.inviterName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {invitationInfo.inviterEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* ロール・権限情報 */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, h: '100%' }}>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <AssignmentIcon color="primary" />
                        <Typography variant="h6">あなたのロール</Typography>
                      </Box>
                      
                      <Box>
                        <Chip
                          label={ROLE_DISPLAY_NAMES[invitationInfo.role]}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {ROLE_DESCRIPTIONS[invitationInfo.role]}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* 招待詳細 */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <ScheduleIcon color="primary" />
                        <Typography variant="h6">招待詳細</Typography>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            招待日時
                          </Typography>
                          <Typography variant="body1">
                            {new Date(invitationInfo.invitedAt).toLocaleString('ja-JP')}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            有効期限
                          </Typography>
                          <Typography 
                            variant="body1"
                            color={isExpired ? 'error.main' : 'text.primary'}
                          >
                            {new Date(invitationInfo.expiresAt).toLocaleString('ja-JP')}
                          </Typography>
                        </Grid>
                      </Grid>

                      {invitationInfo.message && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            メッセージ
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Typography variant="body2">
                              {invitationInfo.message}
                            </Typography>
                          </Paper>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* セキュリティノート */}
            <Alert severity="info" icon={<SecurityIcon />} sx={{ mb: 4 }}>
              この招待を承認すると、指定された組織にメンバーとして参加し、
              割り当てられたロールに応じた権限でシステムにアクセスできるようになります。
            </Alert>

            {/* アクションボタン */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleAcceptInvitation}
                disabled={Boolean(isProcessing || isExpired || isInvalidStatus)}
                startIcon={<CheckCircleIcon />}
                sx={{ minWidth: 160 }}
              >
                {isProcessing ? '処理中...' : '承認して参加'}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={handleDeclineInvitation}
                disabled={Boolean(isProcessing || isInvalidStatus)}
                startIcon={<CancelIcon />}
                sx={{ minWidth: 160 }}
              >
                {isProcessing ? '処理中...' : '拒否'}
              </Button>
            </Stack>

            {/* フッター情報 */}
            <Divider sx={{ mt: 4, mb: 3 }} />
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              この招待リンクは他の人と共有しないでください。
              <br />
              問題が発生した場合は、招待者に直接お問い合わせください。
            </Typography>
          </CardContent>
        </Card>
      </motion.div>
    </Container>
  );
};

export default JoinOrganization;